import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { ApolloContext, compose } from 'react-apollo';
import { withTheme } from 'theming';
import { get } from 'object-path';
import noop from 'lodash/noop';

import configuration from 'Configuration/index';
import { withState } from 'MoshtixShared/component-context-provider';
import { isEventBlackListed } from 'MoshtixShared/helper-ticket-assign-blacklist';
import { DropDownButtonOption, DropDownButtonWrapper } from 'MoshtixShared/component-drop-down-button';
import { Div, Span, A } from 'MoshtixShared/component-element';
import { Icon } from 'MoshtixShared/component-icon';
import { CheckBox } from 'MoshtixShared/component-check-box';
import { Button } from 'MoshtixShared/component-button';
import { ThemeInterface } from 'Types/theme';

import { history } from '../../../app/history';
import { TicketDistroContext } from '../../../app/contexts';
import { EventInterface, TicketInterface } from '../../../types/app';
import styles from './styles';
import { AssignTicketDialog } from '../component-assign-ticket-dialog';
import { RevokeAssignTicketDialog } from '../component-revoke-assigned-ticket-dialog';

interface TicketListItemProps {
  ticket: TicketInterface;
  theme: ThemeInterface;
  order: any;
  onChange: ({ id }: { id: number }) => void;
  checked: boolean;
  canResell: boolean;
  event?: EventInterface;
  // this means that the customer accepted
  isFromAssignedList?: boolean;
  disableTicketDialog: boolean;
}

const TicketListItemWithoutThemeAndState: FC<TicketListItemProps> = ({
  ticket,
  theme,
  onChange,
  checked,
  order,
  event,
  disableTicketDialog = false,
  canResell = true,
  isFromAssignedList = false,
}) => {
  const { ticketDistroContext } = useContext(TicketDistroContext);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const { firstName = '', lastName = '', id, walletDownloadUrl, pdfDownloadUrl, email, event: ticketEvent } = ticket;

  const isAlreadyAssigned = !!get(ticket, 'ticketAssign.customer.id') && !get(ticket, 'ticketAssign.revokedDate', null);

  const resaleStatus = get(ticket, 'resale.status', null);
  const isUpForResell = !!get(ticket, 'resale.id', null);

  const returnStatus = get(ticket, 'ticketReturn.status', null);
  const isUpForReturn = !!get(ticket, 'ticketReturn.id', null);

  const eventUrl = history.location.pathname.endsWith('/')
    ? history.location.pathname
    : `${history.location.pathname}/`;

  const ticketLink = `${eventUrl}ticket/${id}`;
  const [isFocused, setIsFocused]: [boolean, (isFocused: boolean) => void] = useState(false);

  // For assigned tickets, use event from ticket; for regular tickets, use event prop
  const eventToUse = isFromAssignedList ? ticketEvent : event;
  const { digitalTicketsOnly } = eventToUse || {};

  const isValidTicket =
    !ticket.scanned &&
    ticket.valid &&
    !get(ticket, 'ticketType.suppressDistribution') &&
    !get(ticket, 'ticketType.collectorTicketCollectableOnly');
  const isPaperTicketOnly = get(ticket, 'ticketType.paperTicketOnly', false);
  const setState = get(ticketDistroContext, 'actions.setState', noop);
  const orderId = order && order.id;

  const ticketSubTitle = useMemo(() => {
    const subtitleCss = styles.ticketNameCss({ theme, checked });
    const icon = <Icon css={styles.ticketIconCss({ theme, checked })} type="attendee" />;

    if (!isFromAssignedList && !isAlreadyAssigned) {
      const showIcon = (firstName && firstName.length) || (lastName && lastName.length);

      return (
        <>
          {showIcon && icon}
          <Span css={subtitleCss} title={`${firstName} ${lastName}`}>
            {firstName} {lastName}
          </Span>
        </>
      );
    }

    return (
      <>
        {icon}
        <Span css={{ ...subtitleCss, ...styles.emailTextCss }} title={email}>
          {email}
        </Span>
      </>
    );
  }, [isFromAssignedList, isAlreadyAssigned, firstName, lastName, email]);

  const handleRevokeDialogClose = useCallback(
    (revokeSuccess?: boolean): void => {
      setShowRevokeDialog(false);
      if (revokeSuccess) {
        // refresh the page
        history.go(0);
      }
    },
    [history, setShowRevokeDialog],
  );

  // ============================================================================
  // TODO: this will be removed when the getTickets api supports a form of cache.
  const eventId = get(order, 'event.id', 0) || get(ticket, 'order.event.id', 0);

  const hideAssignOption = isEventBlackListed(eventId);
  // TODO: this will be removed when the getTickets api supports a form of cache.
  // ============================================================================

  return (
    <>
      {showAssignDialog && <AssignTicketDialog onClose={() => setShowAssignDialog(false)} ticket={ticket} />}
      {showRevokeDialog && <RevokeAssignTicketDialog onClose={handleRevokeDialogClose} ticket={ticket} />}
      <Div css={styles.cardContainerCss({ isFocused })}>
        <CheckBox
          css={styles.checkBoxCss({ theme })}
          onChange={(e): void => {
            e.stopPropagation();
            onChange({ ticketId: ticket.id });
          }}
          checked={checked}
        />
        <A
          onClick={(e: Event): void => {
            e.preventDefault();
            e.stopPropagation();
            if (disableTicketDialog) {
              window.open(pdfDownloadUrl, '_blank');
            } else {
              history.push(ticketLink);
            }
          }}
          href={disableTicketDialog ? pdfDownloadUrl : ticketLink}
          css={styles.cardCss({ theme, checked })}
        >
          <Div css={styles.flagCss({ theme })} />
          <Div css={styles.nameContainerCss({ theme })}>
            <Div title={ticket.ticketTypeName} css={styles.ticketTypeNameCss({ theme })}>
              {ticket.ticketTypeName}
            </Div>
            <Div css={styles.nameAndIconCss({ theme })}>{ticketSubTitle}</Div>
          </Div>
        </A>
        <DropDownButtonWrapper
          css={styles.extraOptionsCss}
          onBlur={() => setIsFocused(false)}
          options={[
            !isFromAssignedList && !get(ticket, 'ticketType.collectorTicketCollectableOnly') && (
              <DropDownButtonOption
                onClick={() => {
                  setIsFocused(false);
                  window.open(
                    `https://${configuration.publicWebsiteOriginalUrl}/v2/account/reissue-my-ticket/${orderId || ''}`,
                    '_blank',
                  );
                }}
                iconLeft="attendee"
                iconColor={theme.palette.success}
                css={styles.dropdownOptionCss}
              >
                Change name / DOB
              </DropDownButtonOption>
            ),
            !isFromAssignedList && !isAlreadyAssigned && !hideAssignOption && !get(ticket, 'ticketType.collectorTicketCollectableOnly') && (
              <DropDownButtonOption
                onClick={() => {
                  setIsFocused(false);
                  setShowAssignDialog(true);
                }}
                iconLeft="curved-arrow-right-outlined"
                iconColor={theme.palette.success}
                css={styles.dropdownOptionCss}
                disabled={
                  isAlreadyAssigned ||
                  (isUpForResell && resaleStatus !== 'TAKEN_OFF_RESALE') ||
                  (isUpForReturn && returnStatus !== 'TAKEN_OFF_RETURN')
                }
              >
                Assign ticket
              </DropDownButtonOption>
            ),
            !isFromAssignedList && isAlreadyAssigned && !hideAssignOption && (
              <DropDownButtonOption
                onClick={() => {
                  setIsFocused(false);
                  setShowRevokeDialog(true);
                }}
                iconLeft="curved-arrow-left-outlined"
                iconColor={theme.palette.success}
                css={styles.dropdownOptionCss}
              >
                Revoke Ticket
              </DropDownButtonOption>
            ),
            canResell && !isFromAssignedList && !isAlreadyAssigned && (
              <DropDownButtonOption
                onClick={() => {
                  setIsFocused(false);
                  window.open(
                    `https://${configuration.publicWebsiteOriginalUrl}/v2/account/re-sell-my-ticket/${orderId || ''}`,
                    '_blank',
                  );
                }}
                iconLeft="resell"
                iconColor={theme.palette.success}
                css={styles.dropdownOptionCss}
              >
                Resell
              </DropDownButtonOption>
            ),
            isValidTicket && !isPaperTicketOnly && (
              <DropDownButtonOption
                onClick={() => {
                  setIsFocused(false);
                  window.open(walletDownloadUrl, '_blank');
                }}
                iconLeft="mobile"
                iconColor={theme.palette.success}
                css={styles.dropdownOptionCss}
                disabled={isAlreadyAssigned && !isFromAssignedList}
              >
                Add to wallet
              </DropDownButtonOption>
            ),
            isValidTicket && !digitalTicketsOnly && (
              <DropDownButtonOption
                onClick={() => {
                  setIsFocused(false);
                  window.open(pdfDownloadUrl, '_blank');
                }}
                iconLeft="pdf"
                iconColor={theme.palette.success}
                css={styles.dropdownOptionCss}
              >
                View / Print PDF
              </DropDownButtonOption>
            ),
            isValidTicket && (
              <DropDownButtonOption
                onClick={() => {
                  setState({ isHelpVisible: true, helpOrder: orderId || 0 });
                }}
                iconLeft="question"
                iconColor={theme.palette.success}
                css={styles.dropdownOptionCss}
              >
                Help Form
              </DropDownButtonOption>
            ),
          ]}
        >
          <Div
            css={styles.dropdownTitleCss({ theme })}
            onClick={() => {
              setIsFocused(true);
            }}
          >
            Manage
            <Button
              css={styles.menuButtonCss({ theme })}
              iconLeft="vertical-dots"
              buttonType="iconOnly"
              iconColor={checked ? theme.palette.white : null}
            />
          </Div>
        </DropDownButtonWrapper>
      </Div>
    </>
  );
};

export const TicketListItem = compose(
  withState({
    contextName: 'ticketDistroContext',
    Context: TicketDistroContext,
  }),
  withTheme,
)(TicketListItemWithoutThemeAndState);
